import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useQuery } from "convex/react";
import { useMemo, useRef } from "react";
import type { AssistantConversationListItem } from "../types";

export type ProjectConversations = {
  /** Every conversation the query returned, in the backend's pinned-first, activity order. */
  items: AssistantConversationListItem[];
  /** How many rows carry the blue dot; drives the nav-item and tab-label dots. */
  needsYouCount: number;
  /** No rows have arrived for this project yet — distinct from "arrived, and there are none". */
  isLoading: boolean;
};

const EMPTY_ITEMS: AssistantConversationListItem[] = [];

/**
 * The single subscription every conversation surface reads.
 *
 * `listProjectConversations` already projects each thread's status from its own discovery row
 * (D6), so a list of a hundred conversations costs one query rather than a runtime snapshot
 * per row — the read-amplification lesson of LAT-966. Nothing here re-sorts: the backend's
 * pinned-first, activity-descending order is the order the design shows.
 *
 * **The last rows are held across an argument change.** Convex returns `undefined` the moment
 * the args move, and "Show archived" *is* an argument (an archived conversation is not in the
 * result to be filtered in). Without the hold, ticking that box blanked the table to a loading
 * line and — because the visible rows briefly became empty — pruned the selection the user had
 * just made. Held only within one project: a different `projectId` starts empty rather than
 * showing another project's conversations for a frame.
 */
export function useProjectConversations({
  projectId,
  includeArchived,
}: {
  projectId: Id<"projects"> | null | undefined;
  includeArchived?: boolean;
}): ProjectConversations {
  const result = useQuery(
    api.claudia.listProjectConversations,
    projectId ? { projectId, ...(includeArchived ? { includeArchived: true } : {}) } : "skip"
  );

  const lastRef = useRef<{
    projectId: Id<"projects"> | null;
    items: AssistantConversationListItem[];
  }>({ projectId: null, items: EMPTY_ITEMS });

  if (result !== undefined && projectId) {
    lastRef.current = { projectId, items: result };
  }
  const held =
    projectId && lastRef.current.projectId === projectId ? lastRef.current.items : EMPTY_ITEMS;
  const items = result ?? held;

  return useMemo(
    () => ({
      items,
      needsYouCount: items.reduce(
        (count, item) => (item.status === "needs_you" ? count + 1 : count),
        0
      ),
      isLoading: projectId ? result === undefined && held.length === 0 : false,
    }),
    [held.length, items, projectId, result]
  );
}
